// Section videos as text + icon lines (BRIEFS C12.1, D-106).
//
// One line per video: concept videos first, then question videos, each in the unit's order.
//   play-circle     "Concept video: <title> · N min"
//   question-circle "Question video: <title> · N min"
// (no "· N min" when the length is unknown). Lines are link-styled, full width, at least
// 44 px tall, reachable with the keyboard. Clicking calls onPlay(clip) — the caller opens
// the existing VideoModal pop-up over the page.
import React, { useState } from 'react';
import { clipMinutes } from './VideoModal';
import { IconPlayCircle, IconQuestionCircle } from './icons';
import { C, FONT } from './theme';

export const KIND_LABEL = { concept: 'Concept video', question: 'Question video' };

export function clipKind(clip) {
  return clip && clip.kind === 'question' ? 'question' : 'concept';
}

// "Concept video: DNA replication · 2 min"
export function videoLineText(clip) {
  const mins = clipMinutes(clip);
  const title = (clip && clip.title) || 'Video';
  return `${KIND_LABEL[clipKind(clip)]}: ${title}${mins ? ` · ${mins} min` : ''}`;
}

// concept first, then question; stable inside each kind (keeps the given order)
export function sortClips(clips) {
  const list = clips || [];
  return [...list.filter((c) => clipKind(c) === 'concept'), ...list.filter((c) => clipKind(c) === 'question')];
}

export function VideoIcon({ kind, size = 20 }) {
  return kind === 'question' ? <IconQuestionCircle size={size} /> : <IconPlayCircle size={size} />;
}

export function VideoLine({ clip, onPlay, color = C.primaryDark, fontSize = 15.5 }) {
  const [hot, setHot] = useState(false);
  const kind = clipKind(clip);
  return (
    <button type="button" data-testid="v2-video-line" data-kind={kind} data-clip={clip.id}
      onClick={() => onPlay(clip)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
        padding: '6px 4px', border: 0, background: 'transparent', cursor: 'pointer',
        textAlign: 'left', color, fontFamily: FONT.ui, fontSize, fontWeight: 600, lineHeight: 1.35,
        textDecoration: hot ? 'underline' : 'none', textUnderlineOffset: 3, boxSizing: 'border-box',
      }}>
      <span style={{ display: 'flex', flexShrink: 0 }}><VideoIcon kind={kind} /></span>
      <span style={{ minWidth: 0 }}>{videoLineText(clip)}</span>
    </button>
  );
}

// The small "Videos" block at the start of a unit. Nothing when the unit has no videos.
export default function VideoLines({ clips, onPlay }) {
  const list = sortClips(clips);
  if (!list.length) return null;
  return (
    <div data-testid="v2-videos" role="group" aria-label="Videos" style={{
      display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 16px',
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
    }}>
      <div style={{ fontFamily: FONT.ui, fontSize: 13, fontWeight: 700, color: C.muted, letterSpacing: '.02em' }}>
        Videos
      </div>
      {list.map((clip) => <VideoLine key={clip.id} clip={clip} onPlay={onPlay} />)}
    </div>
  );
}
